import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import UserProfileLink from '../profile/UserProfileLink';
import { useServerStore } from '../../store/serverStore';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

interface MessageContentProps {
  content: string;
}

interface MentionData {
  [username: string]: {
    valid: boolean;
    userId: string;
  };
}

const TRUSTED_DOMAINS_KEY = 'trusted_external_domains';

const MessageContent: React.FC<MessageContentProps> = ({ content }) => {
  const { members } = useServerStore();
  const [mentions, setMentions] = useState<MentionData>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState<string | null>(null);
  const [pendingDomain, setPendingDomain] = useState<string | null>(null);
  const [trustDomain, setTrustDomain] = useState(false);
  const [isOpeningExternal, setIsOpeningExternal] = useState(false);

  const getTrustedDomains = (): Set<string> => {
    try {
      const rawValue = localStorage.getItem(TRUSTED_DOMAINS_KEY);
      if (!rawValue) return new Set();
      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) return new Set();
      return new Set(parsed.filter((value) => typeof value === 'string').map((value) => value.toLowerCase()));
    } catch {
      return new Set();
    }
  };

  const saveTrustedDomain = (domain: string) => {
    const normalizedDomain = domain.toLowerCase();
    const trusted = getTrustedDomains();
    trusted.add(normalizedDomain);
    localStorage.setItem(TRUSTED_DOMAINS_KEY, JSON.stringify(Array.from(trusted)));
  };

  const resolveHttpUrl = (href: string): URL | null => {
    try {
      const parsed = new URL(href);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const openInExternalBrowser = async (url: string) => {
    if (window.api?.openExternalLink) {
      const result = await window.api.openExternalLink(url);
      if (result?.success) return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleLinkClick = async (href: string) => {
    const parsed = resolveHttpUrl(href);
    if (!parsed) return;

    const domain = parsed.hostname.toLowerCase();
    if (getTrustedDomains().has(domain)) {
      await openInExternalBrowser(parsed.toString());
      return;
    }

    setPendingExternalUrl(parsed.toString());
    setPendingDomain(domain);
    setTrustDomain(false);
  };

  const closeExternalModal = () => {
    setPendingExternalUrl(null);
    setPendingDomain(null);
    setTrustDomain(false);
    setIsOpeningExternal(false);
  };

  const confirmOpenExternal = async () => {
    if (!pendingExternalUrl) return;

    try {
      setIsOpeningExternal(true);
      if (trustDomain && pendingDomain) {
        saveTrustedDomain(pendingDomain);
      }
      await openInExternalBrowser(pendingExternalUrl);
      closeExternalModal();
    } catch {
      setIsOpeningExternal(false);
    }
  };

  const markdownComponents = {
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      const safeHref = href || '#';
      return (
        <a
          href={safeHref}
          className="text-blue-400 hover:text-blue-300 hover:underline underline-offset-2"
          onClick={(event) => {
            event.preventDefault();
            if (!href) return;
            void handleLinkClick(href);
          }}
        >
          {children}
        </a>
      );
    },
  };

  const renderInlineMarkdown = (text: string, key: string) => {
    if (!text) return null;

    return (
      <ReactMarkdown
        key={key}
        remarkPlugins={[remarkGfm]}
        components={{
          ...markdownComponents,
          p: ({ children }) => <>{children}</>,
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  useEffect(() => {
    // Extract mentions with regex
    const mentionRegex = /@(\w+)/g;
    const extractedMentions: string[] = [];
    let match;

    // Collect all mentioned usernames
    while ((match = mentionRegex.exec(content)) !== null) {
      const username = match[1];
      if (!extractedMentions.includes(username)) {
        extractedMentions.push(username);
      }
    }

    // If no mentions, exit
    if (extractedMentions.length === 0) {
      return;
    }

    setIsProcessing(true);

    // Generate mention results from server members
    const newMentions: MentionData = {};
    
    extractedMentions.forEach(username => {
      // Find member with this username
      const member = members.find(m => 
        m.user?.username?.toLowerCase() === username.toLowerCase() || m.author?.username?.toLowerCase() === username.toLowerCase()
      );
      
      if (member) {
        // If user exists in server members
        newMentions[username] = { 
          valid: true, 
          userId: member.user_id 
        };
      } else {
        // For usernames not in members or when we don't have data
        // Create a temporary ID based on username
        newMentions[username] = { 
          valid: true, // Consider all mentions valid for UX
          userId: `user-${username}`
        };
      }
    });
    
    setMentions(newMentions);
    setIsProcessing(false);
  }, [content, members]);

  // Format content to handle mentions
  const renderContent = () => {
    // During processing or if no mentions, simply render the Markdown
    if (isProcessing || Object.keys(mentions).length === 0) {
      return (
        <>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content}
          </ReactMarkdown>

          <AlertDialog open={Boolean(pendingExternalUrl)} onOpenChange={(open) => { if (!open) closeExternalModal(); }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Ouvrir un lien externe ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Ce lien va etre ouvert dans votre navigateur par defaut, en dehors de l'application.
                </AlertDialogDescription>
              </AlertDialogHeader>

              {pendingExternalUrl && (
                <div className="rounded-md border border-border-color bg-bg-primary px-3 py-2 text-xs text-text-secondary break-all">
                  {pendingExternalUrl}
                </div>
              )}

              {pendingDomain && (
                <label className="flex items-start gap-2 text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    checked={trustDomain}
                    onChange={(event) => setTrustDomain(event.target.checked)}
                    className="mt-0.5"
                  />
                  <span>Ne plus demander pour ce domaine ({pendingDomain})</span>
                </label>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel disabled={isOpeningExternal}>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={confirmOpenExternal} disabled={isOpeningExternal}>
                  {isOpeningExternal ? 'Ouverture...' : 'Ouvrir'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      );
    }

    // Build the parts of the message with formatted mentions
    const parts: JSX.Element[] = [];
    let lastIndex = 0;
    const mentionRegex = /@(\w+)/g;
    let match;
    
    // Reset the regex
    mentionRegex.lastIndex = 0;
    
    while ((match = mentionRegex.exec(content)) !== null) {
      const username = match[1];
      const fullMatch = match[0]; // @username
      const startIndex = match.index;
      const endIndex = startIndex + fullMatch.length;
      
      // Add the text before the mention
      if (startIndex > lastIndex) {
        const segment = content.substring(lastIndex, startIndex);
        const segmentNode = renderInlineMarkdown(segment, `text-${lastIndex}`);
        if (segmentNode) {
          parts.push(segmentNode);
        }
      }
      
      // Add the mention according to its validity
      if (mentions[username]?.valid) {
        parts.push(
          <UserProfileLink key={`mention-${startIndex}`} userId={mentions[username].userId}>
            <span className="mention">@{username}</span>
          </UserProfileLink>
        );
      } else {
        parts.push(<span key={`mention-${startIndex}`}>@{username}</span>);
      }
      
      lastIndex = endIndex;
    }
    
    // Add the rest of the text
    if (lastIndex < content.length) {
      const trailingSegment = content.substring(lastIndex);
      const trailingSegmentNode = renderInlineMarkdown(trailingSegment, `text-${lastIndex}`);
      if (trailingSegmentNode) {
        parts.push(trailingSegmentNode);
      }
    }
    
    return (
      <>
        <div className="markdown-content">{parts}</div>

        <AlertDialog open={Boolean(pendingExternalUrl)} onOpenChange={(open) => { if (!open) closeExternalModal(); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Ouvrir un lien externe ?</AlertDialogTitle>
              <AlertDialogDescription>
                Ce lien va etre ouvert dans votre navigateur par defaut, en dehors de l'application.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {pendingExternalUrl && (
              <div className="rounded-md border border-border-color bg-bg-primary px-3 py-2 text-xs text-text-secondary break-all">
                {pendingExternalUrl}
              </div>
            )}

            {pendingDomain && (
              <label className="flex items-start gap-2 text-sm text-text-secondary">
                <input
                  type="checkbox"
                  checked={trustDomain}
                  onChange={(event) => setTrustDomain(event.target.checked)}
                  className="mt-0.5"
                />
                <span>Ne plus demander pour ce domaine ({pendingDomain})</span>
              </label>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={isOpeningExternal}>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={confirmOpenExternal} disabled={isOpeningExternal}>
                {isOpeningExternal ? 'Ouverture...' : 'Ouvrir'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  };

  return renderContent();
};

export default MessageContent;